// src/pages/ResetPassword.js
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/reset.css";
import leftImage from "../assets/landing1.png";
import keyIcon from "../assets/lock.png";
import { postJson } from "../network/api";
import { ApiEndpoints } from "../network/config";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get mobile number from previous page (support several possible keys)
  const mobileFromState =
    location?.state?.mobileNumber || location?.state?.mobile || location?.state?.mobileNo;

  // Password states
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // visibility toggles for both fields
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // popup state & timers
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState("success"); // "success" | "error"
  const [popupMessage, setPopupMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const popupTimerRef = useRef(null);
  const redirectTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (popupTimerRef.current) {
        clearTimeout(popupTimerRef.current);
        popupTimerRef.current = null;
      }
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
    };
  }, []);

  const showPopupMessage = (type, message, { autoClose = true, ms = 2000 } = {}) => {
    // clear previous popup timer
    if (popupTimerRef.current) {
      clearTimeout(popupTimerRef.current);
      popupTimerRef.current = null;
    }
    setPopupType(type);
    setPopupMessage(message);
    setShowPopup(true);

    if (autoClose) {
      popupTimerRef.current = setTimeout(() => {
        setShowPopup(false);
        popupTimerRef.current = null;
      }, ms);
    }
  };

  const validatePassword = (pwd) => {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(pwd);
  };

  const handleResetPassword = async (e) => {
    e?.preventDefault?.();

    // Basic checks
    if (!mobileFromState) {
      showPopupMessage("error", "No mobile number found. Go back and retry.");
      return;
    }

    if (!validatePassword(password)) {
      showPopupMessage(
        "error",
        "Password must have 1 uppercase, 1 lowercase, 1 number, 1 symbol, and at least 8 characters."
      );
      return;
    }

    if (password !== confirmPassword) {
      showPopupMessage("error", "Passwords do not match.");
      return;
    }

    // call API
    setLoading(true);
    try {
      const endpoint = ApiEndpoints.updatePassword || "/api/app/updatePassword";
      const res = await postJson(endpoint, {
        mobileNumber: mobileFromState,
        password: password,
      });

      // If API returns explicit failure payload in 200, respect it
      if (res && (res.success === false || res?.error)) {
        const msg =
          res?.error?.message || res?.message || "Failed to update password.";
        showPopupMessage("error", msg);
        setLoading(false);
        return;
      }

      // success - show popup and redirect after 1500ms
      const successMsg =
        (res && (res.message || (res.data && res.data.message))) ||
        "Password updated successfully.";
      showPopupMessage("success", successMsg, { autoClose: true, ms: 1500 });

      // clear any previous redirect timer
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }

      redirectTimerRef.current = setTimeout(() => {
        redirectTimerRef.current = null;
        navigate("/login");
      }, 1500);

      setLoading(false);
      return;
    } catch (err) {
      console.error("updatePassword error:", err);
      // extract message if possible (postJson throws Err with .body)
      let message = "Something went wrong. Please try again.";
      try {
        if (err?.body) {
          const parsed = JSON.parse(err.body);
          message = parsed?.message || parsed?.error?.message || message;
        } else if (err?.message) {
          message = err.message;
        }
      } catch (ex) {
        // ignore
      }
      showPopupMessage("error", message);
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleResetPassword(e);
  };

  return (
    <div className="reset-page">
      {/* Left Image Section */}
      <div className="left-side">
        <img src={leftImage} alt="Lingayath Weds" className="left-image" />
      </div>

      {/* Right Reset Password Section */}
      <div className="right-side">
        <div className="reset-card">
          <h1 className="title">Welcome to Lingayath Weds</h1>

          {/* Display Mobile Number */}
          {mobileFromState && (
            <p className="mobile-display">
              Resetting password for: <b>{mobileFromState}</b>
            </p>
          )}

          <div className="reset-header">
            <div className="icon-container">
              <img src={keyIcon} alt="Reset Password Icon" className="reset-icon" />
            </div>
            <h2 className="reset-title">Reset Password Page</h2>
            <p className="reset-instruction">Please enter your new password</p>
          </div>

          <hr className="divider" />

          {/* Popup Message */}
          {showPopup && (
            <div className={`popup-message ${popupType}`}>
              <div className="popup-card-content">
                <div className="popup-icon">{popupType === "success" ? "✓" : "⚠"}</div>
                <div className="popup-text">
                  <p className="popup-title">{popupMessage}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleResetPassword}>
            <div className="form-group">
              <label className="input-label">New Password*</label>
              <div className="password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter New Password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <span
                  className="eye-icon"
                  onClick={() => setShowPassword((s) => !s)}
                  role="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  ) : (
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </span>
              </div>
            </div>

            <div className="form-group">
              <label className="input-label">Confirm Password*</label>
              <div className="password-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Enter New Password Again"
                  className="form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <span
                  className="eye-icon"
                  onClick={() => setShowConfirmPassword((s) => !s)}
                  role="button"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? (
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  ) : (
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </span>
              </div>
            </div>

            <button
              type="submit"
              className="reset-btn"
              disabled={loading}
            >
              {loading ? "Updating..." : "Reset Password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
